/**
 * Chat Interface Component
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { MessageList } from './MessageList';
import { endpoints } from '../config/api';
import { apiRequest, isAuthConfigured } from '../auth/authConfig';
import type { Message, StreamChunk, DocumentReference, ActiveTool } from '@bsp-chatbot/shared';

interface ChatInterfaceProps {
  conversationId: string | null;
  initialMessages: Message[];
  onCreateConversation: () => string;
  onAddMessage: (conversationId: string, message: Message, setActive?: boolean) => void;
}

export function ChatInterface({
  conversationId,
  initialMessages,
  onCreateConversation,
  onAddMessage,
}: ChatInterfaceProps) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(conversationId);
  const [activeTools, setActiveTools] = useState<ActiveTool[]>([]);
  const [reasoningText, setReasoningText] = useState<string>('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { instance, accounts } = useMsal();

  // Sync messages only when switching to a DIFFERENT conversation (not on creation)
  useEffect(() => {
    // Only sync if we're switching to an existing conversation from outside
    // Don't sync if conversationId is null (new conversation being created)
    if (conversationId !== null && conversationId !== currentConversationId) {
      setMessages(initialMessages);
      setCurrentConversationId(conversationId);
    }
  }, [conversationId]); // Removed initialMessages dependency

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthConfigured) return null;

    try {
      const account = accounts[0];
      if (!account) return null;

      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }, [instance, accounts]);

  const sendMessage = async (content: string) => {
    setError(null);

    // Create conversation if needed
    let convId = currentConversationId;
    if (!convId) {
      convId = onCreateConversation();
      setCurrentConversationId(convId);
    }

    // Add user message immediately
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    // Create placeholder assistant message
    const assistantId = crypto.randomUUID();
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };

    // Set loading state and add messages
    setIsLoading(true);
    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    onAddMessage(convId, userMessage);

    // Yield to allow React to paint before starting async work
    await new Promise(resolve => setTimeout(resolve, 0));

    try {
      const token = await getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(endpoints.chatStream, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: content,
          conversationId: convId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let assistantContent = '';
      let thinking = '';
      const documentReferences: DocumentReference[] = [];
      let buffer = '';
      let runningToolCount = 0; // Track how many tools are currently running
      let hasUsedTools = false; // Track if any tools have been called
      let textChunksSinceToolEnd = 0; // Count text chunks after last tool_end
      const RESPONSE_THRESHOLD = 3; // After this many chunks without tool_start, it's the response

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process all complete lines (SSE format: data: {...}\n\n)
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data: StreamChunk = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'text':
                  assistantContent += data.content || '';
                  textChunksSinceToolEnd++;

                  // Text goes to ProcessingStatus (reasoning) if:
                  // - No tools have been used yet (pre-tool reasoning), OR
                  // - Tools are currently running, OR
                  // - We haven't seen enough chunks since last tool_end (might be between-tools text)
                  // Text goes to message bubble (response) if:
                  // - Tools have been used AND no tools running AND enough chunks seen
                  const isResponsePhase = hasUsedTools && runningToolCount === 0 && textChunksSinceToolEnd > RESPONSE_THRESHOLD;

                  if (isResponsePhase) {
                    // Clear reasoning text - we're now in response mode
                    setReasoningText('');
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === assistantId ? { ...m, content: assistantContent } : m
                      )
                    );
                  } else {
                    setReasoningText(assistantContent);
                  }
                  // Yield to allow React to paint
                  await new Promise(resolve => requestAnimationFrame(resolve));
                  break;

                case 'thinking':
                  thinking += data.content || '';
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId ? { ...m, thinking } : m
                    )
                  );
                  break;

                case 'document_reference':
                  if (data.reference) {
                    documentReferences.push(data.reference);
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === assistantId
                          ? { ...m, documentReferences: [...documentReferences] }
                          : m
                      )
                    );
                  }
                  break;

                case 'tool_start':
                  runningToolCount++;
                  hasUsedTools = true;
                  textChunksSinceToolEnd = 0; // Reset - any buffered text was reasoning
                  // Clear previous content - it was reasoning, start fresh
                  assistantContent = '';
                  setReasoningText('');
                  if (data.toolName && data.toolId) {
                    setActiveTools((prev) => [
                      ...prev,
                      { toolName: data.toolName!, toolId: data.toolId!, status: 'running' as const },
                    ]);
                  }
                  break;

                case 'tool_end':
                  runningToolCount = Math.max(0, runningToolCount - 1);
                  textChunksSinceToolEnd = 0; // Reset counter, start fresh after this tool
                  if (data.toolId) {
                    setActiveTools((prev) =>
                      prev.map((tool) =>
                        tool.toolId === data.toolId
                          ? { ...tool, status: data.success ? 'success' : 'error' }
                          : tool
                      )
                    );
                    // Clear content - next text will be either more reasoning or the response
                    assistantContent = '';
                    setReasoningText('');
                  }
                  break;

                case 'error':
                  setError(data.error || 'Unknown error');
                  break;

                case 'done':
                  // Clear active tools and reasoning
                  setActiveTools([]);
                  setReasoningText('');
                  // Final update and save to storage
                  const finalMessage: Message = {
                    id: assistantId,
                    role: 'assistant',
                    content: assistantContent,
                    timestamp: new Date().toISOString(),
                    thinking: thinking || undefined,
                    documentReferences: documentReferences.length > 0 ? documentReferences : undefined,
                  };
                  setMessages((prev) =>
                    prev.map((m) => (m.id === assistantId ? finalMessage : m))
                  );
                  // Set active=true to update the key/active conversation AFTER streaming completes
                  onAddMessage(convId!, finalMessage, true);
                  break;
              }
            } catch {
              // Ignore parse errors for partial chunks
            }
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Remove the placeholder messages on error
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id && m.id !== assistantId));
      setActiveTools([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const message = input.trim();
    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Example prompts
  const examplePrompts = [
    { icon: '📊', text: 'How do I develop SDTM datasets?' },
    { icon: '🎲', text: 'What is the randomization QC process?' },
    { icon: '📋', text: 'How do I set up TLF shells?' },
    { icon: '📑', text: 'List all SOPs and their purposes' },
  ];

  return (
    <div className="flex-1 flex flex-col w-full overflow-hidden">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center p-6">
            <div className="max-w-2xl w-full">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  How can I help you today?
                </h2>
                <p className="text-gray-600">
                  Ask me about BSP processes, SOPs, work instructions, or templates.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {examplePrompts.map((prompt) => (
                  <button
                    key={prompt.text}
                    onClick={() => {
                      setInput(prompt.text);
                      inputRef.current?.focus();
                    }}
                    className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all text-left group"
                  >
                    <span className="text-2xl">{prompt.icon}</span>
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {prompt.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <MessageList messages={messages} isLoading={isLoading} activeTools={activeTools} reasoningText={reasoningText} />
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="mx-4 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-red-700 text-sm flex-1">{error}</span>
          <button
            onClick={clearError}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Input */}
      <div className="border-t bg-white p-4 flex-shrink-0">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about BSP processes, SOPs, or work instructions..."
              className="w-full px-4 py-3 pr-24 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-gray-50 focus:bg-white transition-colors overflow-hidden"
              rows={1}
              disabled={isLoading}
            />
            <div className="absolute right-2 bottom-2 flex items-center gap-2">
              {isLoading ? (
                <div className="flex items-center gap-2 text-primary-600 px-3 py-1.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span className="text-sm">Thinking...</span>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </form>
      </div>
    </div>
  );
}
